import { errorCodeOf, errorDetailsOf } from "@luckymenu/api-client";
import { useMutation, useQuery, useQueryClient, useTRPC } from "@luckymenu/api-client/react";
import { uuidv7 } from "@luckymenu/core";
import { useCallback, useState } from "react";
import { useCan, usePlanIncludes } from "./use-navigation";

/** Why closing or printing did not work, for the screen's words (`reports:dayClose.problems.*`). */
export type DayCloseProblem =
	| "future"
	| "notClosed"
	| "noPrinter"
	| "printerOff"
	| "notInPlan"
	| "offline"
	| "failed";

export function dayCloseProblemOf(error: unknown): DayCloseProblem {
	const reason = errorDetailsOf(error).reason;
	if (reason === "future") return "future";
	if (reason === "not_closed") return "notClosed";
	if (reason === "no_printer") return "noPrinter";
	if (reason === "printer_inactive") return "printerOff";
	if (reason === "not_in_plan") return "notInPlan";
	if (errorCodeOf(error) === "NETWORK") return "offline";
	return "failed";
}

/**
 * More > Day close (P11-T07): a business date's totals now and its closing,
 * Close (stores the totals; again replaces them) and Print (a print job of
 * the closing on the bill printer, which the print service prints). Owners,
 * managers and cashiers, on a plan with billing; printing needs `printing`.
 */
export function useDayClose(businessDate: string) {
	const trpc = useTRPC();
	const queryClient = useQueryClient();
	// Both hooks always run: `&&` would skip the second until the membership loads.
	const mayClose = useCan("day.close");
	const hasBilling = usePlanIncludes("billing");
	const allowed = mayClose && hasBilling;
	const canPrint = usePlanIncludes("printing");
	const options = trpc.staff.dayClose.get.queryOptions({ businessDate });
	const query = useQuery({ ...options, enabled: allowed });
	const [problem, setProblem] = useState<DayCloseProblem | null>(null);
	const [printed, setPrinted] = useState(false);
	const close = useMutation(
		trpc.staff.dayClose.close.mutationOptions({
			onSuccess: (data) => queryClient.setQueryData(options.queryKey, data),
		}),
	);
	const print = useMutation(trpc.staff.dayClose.print.mutationOptions());

	const closeDay = useCallback(async () => {
		setProblem(null);
		setPrinted(false);
		try {
			await close.mutateAsync({ businessDate });
			return true;
		} catch (error) {
			setProblem(dayCloseProblemOf(error));
			return false;
		}
	}, [close, businessDate]);

	const printDay = useCallback(async () => {
		setProblem(null);
		setPrinted(false);
		try {
			await print.mutateAsync({ id: uuidv7(), businessDate });
			setPrinted(true);
			return true;
		} catch (error) {
			setProblem(dayCloseProblemOf(error));
			return false;
		}
	}, [print, businessDate]);

	return {
		status: query.data
			? ("ready" as const)
			: query.isError
				? ("error" as const)
				: ("loading" as const),
		data: query.data ?? null,
		retry: () => void query.refetch(),
		close: closeDay,
		closing: close.isPending,
		print: printDay,
		printing: print.isPending,
		/** The plan includes printing. */
		canPrint,
		/** The last print job was made; the print service prints it. */
		printed,
		problem,
	};
}
